import {FormikProps} from 'formik';
import FormikSelectField from '../../../../components/formikFileds/formikSelectField';
import UseGetProvince from '../../../../utilities/hooks/province/useGetProvince';
import WarningMessage from '../../components/WarningMessage';
import {IInfoFormValues} from '../infoFormValues';

interface IProps {
  form: FormikProps<IInfoFormValues>;
}

const RecipientInfoField = ({form}: IProps) => {
  const {provinces} = UseGetProvince();

  const provincesOptions =
    provinces?.map(province => ({
      label: province.name,
      value: province.id.toString(),
    })) ?? [];

  const citiesOptions =
    provinces
      ?.find(state => state?.id.toString() === form?.values?.state)
      ?.cities?.map(city => ({
        label: city.name,
        value: city.id.toString(),
      })) ?? [];

  // Helper: check if field has error AND is touched
  const hasError = (field: keyof IInfoFormValues) =>
    form.touched[field] && form.errors[field];

  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium text-gray-800 text-md">اطلاعات تحویل</span>

      {/* Recipient Name */}
      <div>
        <span className="text-sm text-gray-800">
          نام و نام خانوادگی گیرنده <span className="text-red-500">*</span>
        </span>
        <input
          className={`w-full h-10 pl-10 pr-3 my-2 border border-solid rounded-lg text-sm text-gray-800 focus:outline-none ${
            hasError('recipientName') ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="نام و نام خانوادگی گیرنده را وارد کنید"
          name="recipientName"
          value={form.values.recipientName ?? ''}
          onChange={e =>
            form.setFieldValue('recipientName', e.target.value, true)
          }
          onBlur={() => form.setFieldTouched('recipientName', true)}
        />
        {hasError('recipientName') && (
          <div className="text-xs text-red-500">
            {form.errors.recipientName}
          </div>
        )}
      </div>

      <FormikSelectField
        name="state"
        label="استان گیرنده"
        placeholder="استان گیرنده را انتخاب کنید"
        required
        formik={form}
        options={provincesOptions}
        onValueChange={() => form.setFieldValue('city', '', true)}
      />

      <FormikSelectField
        name="city"
        label="شهرستان گیرنده"
        placeholder="شهرستان گیرنده را انتخاب کنید"
        required
        formik={form}
        disabled={!Boolean(form.values.state)}
        options={citiesOptions}
      />

      {/* Address */}
      <div>
        <span className="text-sm text-gray-800">
          آدرس گیرنده <span className="text-red-500">*</span>
        </span>
        <input
          className={`w-full h-10 pl-10 pr-3 my-2 border border-solid rounded-lg text-sm text-gray-800 focus:outline-none ${
            hasError('address') ? 'border-red-500' : 'border-gray-300'
          }`}
          name="address"
          placeholder="آدرس گیرنده را وارد کنید"
          value={form.values.address ?? ''}
          onChange={e => form.setFieldValue('address', e.target.value, true)}
          onBlur={() => form.setFieldTouched('address', true)}
        />
        {hasError('address') && (
          <div className="text-xs text-red-500">{form.errors.address}</div>
        )}
      </div>

      {/* Plate Number */}
      <div>
        <span className="text-sm text-gray-800">
          شماره پلاک <span className="text-red-500">*</span>
        </span>
        <input
          className={`w-full h-10 pl-10 pr-3 my-2 border border-solid rounded-lg text-sm focus:outline-none ${
            hasError('plateNumber') ? 'border-red-500' : 'border-gray-300'
          }`}
          name="plateNumber"
          value={form.values.plateNumber ?? ''}
          type="number"
          placeholder="شماره پلاک را وارد کنید"
          inputMode="numeric"
          onChange={e => {
            const value = e.target.value;
            if (value.length > 7) return;
            form.setFieldValue(
              'plateNumber',
              value === '' ? null : Number(value),
              true,
            );
          }}
          onBlur={() => form.setFieldTouched('plateNumber', true)}
        />
        {hasError('plateNumber') && (
          <div className="text-xs text-red-500">{form.errors.plateNumber}</div>
        )}
      </div>

      {/* Postal Code */}
      <div>
        <span className="text-sm text-gray-800">
          کد پستی <span className="text-red-500">*</span>
        </span>
        <input
          className={`w-full h-10 pl-10 pr-3 my-2 border border-solid rounded-lg text-sm focus:outline-none ${
            hasError('postalCode') ? 'border-red-500' : 'border-gray-300'
          }`}
          name="postalCode"
          value={form.values.postalCode ?? ''}
          type="number"
          placeholder="کد پستی را وارد کنید"
          inputMode="numeric"
          maxLength={10}
          onChange={e => {
            const value = e.target.value;
            if (value.length <= 10) {
              form.setFieldValue(
                'postalCode',
                value === '' ? null : Number(value),
                true,
              );
            }
          }}
          onBlur={() => form.setFieldTouched('postalCode', true)}
        />
        {hasError('postalCode') && (
          <div className="text-xs text-red-500">{form.errors.postalCode}</div>
        )}
        {/* Fallback warning for length - remove if using Yup */}
        {form.touched.postalCode &&
          !form.errors.postalCode &&
          form.values.postalCode?.toString()?.length !== 10 &&
          form.values.postalCode !== null && (
            <WarningMessage title="شماره پستی کاربر باید 10 رقم باشد" />
          )}
      </div>
    </div>
  );
};

export default RecipientInfoField;
